import React, { useState, useRef, useEffect } from 'react';
import { Button, Alert, Spinner, Card, CardHeader, CardBody, FormGroup, Label, Input } from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';

export const AdminOriginalDataPackage = () => {
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const fileInputRef = useRef(null);

  // CTRAC Package state
  const [ctracLoading, setCtracLoading] = useState(false);
  const [ctracUploadLoading, setCtracUploadLoading] = useState(false);
  const [ctracDeleteLoading, setCtracDeleteLoading] = useState(false);
  const [ctracError, setCtracError] = useState(null);
  const [ctracSuccess, setCtracSuccess] = useState(null);
  const [ctracMetadata, setCtracMetadata] = useState(null);
  const ctracFileInputRef = useRef(null);

  // Load metadata on component mount
  useEffect(() => {
    loadMetadata();
    loadCtracMetadata();
  }, []);

  const loadMetadata = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getOriginalPackageMetadata();
      setMetadata(data);
    } catch (exc) {
      console.error('Failed to load metadata:', exc);
      // Don't set error here - it's normal if no package exists yet
      setMetadata(null);
    } finally {
      setLoading(false);
    }
  };

  const loadCtracMetadata = async () => {
    try {
      setCtracLoading(true);
      setCtracError(null);
      const data = await apiClient.getOriginalCtracPackageMetadata();
      setCtracMetadata(data);
    } catch (exc) {
      console.error('Failed to load CTRAC metadata:', exc);
      // Don't set error here - it's normal if no package exists yet
      setCtracMetadata(null);
    } finally {
      setCtracLoading(false);
    }
  };

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    if (!file.name.endsWith('.zip')) {
      setError('Invalid file format. Please select a .zip file');
      return;
    }

    if (!window.confirm('Upload this package? This will replace any existing package in the storage.')) {
      return;
    }

    try {
      setUploadLoading(true);
      setError(null);
      setSuccess(null);

      await apiClient.uploadOriginalPackage(file);
      setSuccess('Package uploaded and extracted successfully!');

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Reload metadata
      await loadMetadata();
    } catch (exc) {
      console.error('Failed to upload package:', exc);
      setError(exc.message || 'Failed to upload package');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove the original data package? The OriginalPackage folder and all of its contents will be permanently deleted.')) {
      return;
    }

    try {
      setDeleteLoading(true);
      setError(null);
      setSuccess(null);

      const result = await apiClient.deleteOriginalPackage();
      setSuccess(result?.message || 'Original data package removed successfully.');

      // Reload metadata
      await loadMetadata();
    } catch (exc) {
      console.error('Failed to delete package:', exc);
      setError(exc.message || 'Failed to delete package');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCtracUpload = async () => {
    const file = ctracFileInputRef.current?.files?.[0];
    if (!file) {
      setCtracError('Please select a file to upload');
      return;
    }

    if (!file.name.endsWith('.zip')) {
      setCtracError('Invalid file format. Please select a .zip file');
      return;
    }

    if (!window.confirm('Upload this CTRAC package? This will replace any existing CTRAC package in the storage.')) {
      return;
    }

    try {
      setCtracUploadLoading(true);
      setCtracError(null);
      setCtracSuccess(null);

      await apiClient.uploadOriginalCtracPackage(file);
      setCtracSuccess('CTRAC package uploaded successfully!');

      // Clear file input
      if (ctracFileInputRef.current) {
        ctracFileInputRef.current.value = '';
      }

      // Reload metadata
      await loadCtracMetadata();
    } catch (exc) {
      console.error('Failed to upload CTRAC package:', exc);
      setCtracError(exc.message || 'Failed to upload CTRAC package');
    } finally {
      setCtracUploadLoading(false);
    }
  };

  const handleCtracDelete = async () => {
    if (!window.confirm('Remove the original CTRAC package? The OriginalCtracPackage folder and all of its contents will be permanently deleted.')) {
      return;
    }

    try {
      setCtracDeleteLoading(true);
      setCtracError(null);
      setCtracSuccess(null);

      const result = await apiClient.deleteOriginalCtracPackage();
      setCtracSuccess(result?.message || 'Original CTRAC package removed successfully.');

      // Reload metadata
      await loadCtracMetadata();
    } catch (exc) {
      console.error('Failed to delete CTRAC package:', exc);
      setCtracError(exc.message || 'Failed to delete CTRAC package');
    } finally {
      setCtracDeleteLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Original Data Package</h2>
      </div>

      <Alert color="danger" className="mb-3" isOpen={!!error} toggle={() => setError(null)} fade={false}>
        <strong>Error:</strong> {error}
      </Alert>

      <Alert color="success" className="mb-3" isOpen={!!success} toggle={() => setSuccess(null)} fade={false}>
        <strong>Success:</strong> {success}
      </Alert>

      {/* Current Package Info */}
      <Card className="mb-3">
        <CardHeader>
          <h5 className="mb-0">Current Package Information</h5>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-3">
              <Spinner size="sm" className="me-2" />
              <span>Loading...</span>
            </div>
          ) : metadata ? (
            <div>
              <p className="mb-2">
                <strong>Upload Time:</strong> {formatDateTime(metadata.UploadTime)}
              </p>
              <p className="mb-2">
                <strong>File Name:</strong> {metadata.FileName || 'N/A'}
              </p>
              {metadata.UploadedByUserName && (
                <p className="mb-0">
                  <strong>Uploaded By:</strong> {metadata.UploadedByUserName}
                </p>
              )}
            </div>
          ) : (
            <div className="text-muted">
              No package has been uploaded yet.
            </div>
          )}
        </CardBody>
      </Card>

      {/* Upload New Package */}
      <Card className="mb-4">
        <CardHeader>
          <h5 className="mb-0">Upload New Package</h5>
        </CardHeader>
        <CardBody>
          <FormGroup>
            <Label for="packageFile">Select ZIP file</Label>
            <Input
              type="file"
              id="packageFile"
              innerRef={fileInputRef}
              accept=".zip"
              disabled={uploadLoading}
            />
            <small className="form-text text-muted">
              Select a .zip file to upload. The package will be extracted to the configured storage location.
            </small>
          </FormGroup>

          <Button
            color="primary"
            onClick={handleUpload}
            disabled={uploadLoading}
          >
            {uploadLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              'Upload Package'
            )}
          </Button>
          <Button
            color="danger"
            className="ms-2"
            onClick={handleDelete}
            disabled={deleteLoading || uploadLoading || !metadata}
            title={!metadata ? 'Nothing to delete' : 'Permanently remove the OriginalPackage folder and all of its contents'}
          >
            {deleteLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Removing...
              </>
            ) : (
              'Delete Package'
            )}
          </Button>
        </CardBody>
      </Card>

      {/* CTRAC Package Section */}
      <div className="d-flex justify-content-between align-items-center mb-3 mt-5">
        <h2 className="mb-0">Original CTRAC Package</h2>
      </div>

      <Alert color="danger" className="mb-3" isOpen={!!ctracError} toggle={() => setCtracError(null)} fade={false}>
        <strong>Error:</strong> {ctracError}
      </Alert>

      <Alert color="success" className="mb-3" isOpen={!!ctracSuccess} toggle={() => setCtracSuccess(null)} fade={false}>
        <strong>Success:</strong> {ctracSuccess}
      </Alert>

      {/* Current CTRAC Package Info */}
      <Card className="mb-3">
        <CardHeader>
          <h5 className="mb-0">Current CTRAC Package Information</h5>
        </CardHeader>
        <CardBody>
          {ctracLoading ? (
            <div className="text-center py-3">
              <Spinner size="sm" className="me-2" />
              <span>Loading...</span>
            </div>
          ) : ctracMetadata ? (
            <div>
              <p className="mb-2">
                <strong>Upload Time:</strong> {formatDateTime(ctracMetadata.UploadTime)}
              </p>
              <p className="mb-2">
                <strong>File Name:</strong> {ctracMetadata.FileName || 'N/A'}
              </p>
              <p className="mb-2">
                <strong>MD5 Hash:</strong> <code>{ctracMetadata.Md5Hash || 'N/A'}</code>
              </p>
              {ctracMetadata.UploadedByUserName && (
                <p className="mb-0">
                  <strong>Uploaded By:</strong> {ctracMetadata.UploadedByUserName}
                </p>
              )}
            </div>
          ) : (
            <div className="text-muted">
              No CTRAC package has been uploaded yet.
            </div>
          )}
        </CardBody>
      </Card>

      {/* Upload New CTRAC Package */}
      <Card>
        <CardHeader>
          <h5 className="mb-0">Upload New CTRAC Package</h5>
        </CardHeader>
        <CardBody>
          <FormGroup>
            <Label for="ctracPackageFile">Select ZIP file</Label>
            <Input
              type="file"
              id="ctracPackageFile"
              innerRef={ctracFileInputRef}
              accept=".zip"
              disabled={ctracUploadLoading}
            />
            <small className="form-text text-muted">
              Select a .zip file to upload. The package will be stored as-is (not extracted) in the configured storage location with MD5 hash verification.
            </small>
          </FormGroup>

          <Button
            color="primary"
            onClick={handleCtracUpload}
            disabled={ctracUploadLoading}
          >
            {ctracUploadLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              'Upload CTRAC Package'
            )}
          </Button>
          <Button
            color="danger"
            className="ms-2"
            onClick={handleCtracDelete}
            disabled={ctracDeleteLoading || ctracUploadLoading || !ctracMetadata}
            title={!ctracMetadata ? 'Nothing to delete' : 'Permanently remove the OriginalCtracPackage folder and all of its contents'}
          >
            {ctracDeleteLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Removing...
              </>
            ) : (
              'Delete CTRAC Package'
            )}
          </Button>
        </CardBody>
      </Card>
    </div>
  );
};
